import type { RotationApprovalPayload, RotationPhasePayload, RotationSchedulePayload } from '@/hooks/useWebSocket';

/**
 * THE SHAPES CONTINUOUS COVERAGE IS READ AND WRITTEN IN (#248, #250).
 *
 * Types only, no runtime — extracted verbatim from `MissionCoveragePanel.tsx` so the panel, the
 * formatters that normalise these shapes and the presentation maps that colour them can all name
 * the same thing without importing a 3,700-line component to do it.
 */

/**
 * What a relief launch is COMMANDED to fly on its way to the station (044, #251 stage 4).
 *
 * The server's `ReliefApproach`, mirrored: `full_route` is the whole stored route from waypoint 1,
 * `station_only` is the last waypoint alone, and `outgoing_position` is the live fix of the
 * aircraft being relieved. Only the relief's inbound leg - no mode changes the altitude it holds.
 */
export type ReliefApproach = 'full_route' | 'station_only' | 'outgoing_position';

/** The picker's order, which is also shortest-approach-last. */
export const RELIEF_APPROACH_OPTIONS: readonly ReliefApproach[] = [
  'full_route', 'station_only', 'outgoing_position',
];

/** The label an operator reads for each mode, in the panel and on the picker. */
export const RELIEF_APPROACH_LABEL: Record<ReliefApproach, string> = {
  full_route: 'FLY THE WHOLE ROUTE',
  station_only: 'GO STRAIGHT TO THE STATION',
  outgoing_position: 'GO TO THE AIRCRAFT ON STATION',
};

/** What each mode actually does, in one sentence, stated as the consequence the operator buys. */
export const RELIEF_APPROACH_HELP: Record<ReliefApproach, string> = {
  full_route: 'The relief flies the mission from its first waypoint, as planned. Choose this when '
    + 'the approach leg exists for a reason.',
  station_only: 'The relief goes direct to the last waypoint, skipping the approach. The shortest '
    + 'way to get a second aircraft over the station.',
  outgoing_position: 'The relief goes direct to where the aircraft on station actually is. Falls '
    + 'back to the last waypoint if that aircraft has drifted off station or gone quiet.',
};

export interface CoverageRow {
  id: string;
  mission_id: string;
  target_lat: number | null;
  target_lon: number | null;
  target_radius_m: number | null;
  target_geofence_id: string | null;
  station_altitude_m: number;
  window_start: string | null;
  window_end: string | null;
  coverage_target_pct: number;
  handover_policy: 'propose' | 'full_auto';
  battery_normal_pct: number;
  battery_critical_pct: number;
  critical_action: 'alert_only' | 'auto_rth';
  /**
   * What a relief flies on its way to the station (044).
   *
   * OPTIONAL for the rolling-deploy reason the rotation row's phase columns are: this client can
   * be talking to an API that predates 044 and sends no such field. Every read site treats an
   * absent value as `full_route`, which is what that API's own launches fly.
   */
  relief_approach?: ReliefApproach;
  state: 'pending_station' | 'covered' | 'handover' | 'gap' | 'suspended' | 'stopped';
  state_reason: string | null;
  covered_by_drone_id: string | null;
}

/**
 * ONE row of `rotation_actions` as the rotation endpoint returns it (#250 W6).
 *
 * `inputs` is the whole record behind the forecast — §6's "every output carries its inputs",
 * widened by §6.1 rule 2 to carry their SOURCE as well. It is `unknown`-shaped on purpose: the
 * server owns that blob's schema and the panel reads named fields out of it defensively, rather
 * than mirroring a shape that would go stale silently the first time the planner adds a term.
 */
export interface RotationActionRow {
  id: string;
  cycle_no: number;
  action: string;
  planned_at: string;
  commit_state: 'tentative' | 'committed' | 'executing' | 'done' | 'failed' | 'cancelled';
  inputs: Record<string, unknown> | null;
  /**
   * The relief named on this action, and where the launch has got to (W2, and 032 for the last
   * two). Mirrored here — unlike the four `approved_*` columns deliberately left off — because
   * none of them is a VERDICT: they are the row's own values, and the panel renders them rather
   * than recomputing anything from them.
   *
   * OPTIONAL, and that is not laziness. During a rolling deploy this client can be talking to an
   * API that predates 032, whose rows carry neither field; `?? 'scheduled'` at the read site is
   * what makes that a forecast with no phase rather than a crash.
   */
  drone_id?: string | null;
  phase?: RotationPhasePayload['phase'] | null;
  phase_reason?: RotationPhasePayload['phase_reason'];
  /** When the row last moved — the closest thing to "when did this fail". */
  updated_at?: string;
}

export interface RotationForecastView {
  current: RotationActionRow | null;
  history: RotationActionRow[];
  margin_decisions: unknown[];
  /**
   * Whether an operator has armed `current`, and whether that approval still holds (W3).
   *
   * DERIVED ON THE SERVER and sent whole. The four `approved_*` columns are deliberately not on
   * `RotationActionRow` here: recomputing "is this still approved" on the client would be a second
   * implementation of the one question the launcher also answers, and the two disagreeing is a
   * panel showing APPROVED over an aircraft the server will refuse to fly.
   */
  approval: RotationApprovalPayload | null;
  /**
   * Whether THIS DEPLOYMENT launches replacements by itself (W4).
   *
   * Optional for the rolling-deploy reason the phase columns are: an API that predates W4 does not
   * send it, and the read site below treats an absent value as OFF — the cautious direction, since
   * the copy it drives tells an operator whether they still have to launch the aircraft by hand.
   */
  automatic_launch_enabled?: boolean;
}

/**
 * The forecast as this panel renders it, from EITHER source.
 *
 * The socket frame and the endpoint say the same thing in two shapes — the frame flattens what the
 * row keeps in `inputs` — so both are normalised here and the render never has to know which one
 * it got. The frame is the live one and wins; the endpoint is the load and the backstop, exactly
 * the split the coverage line above already uses.
 */
export interface ReplacementView {
  decision: RotationSchedulePayload['decision'];
  reason: string | null;
  launchAt: string | null;
  outgoingMustLeaveAt: string | null;
  commitDeadline: string | null;
  commitState: 'tentative' | 'committed' | null;
  marginSource: 'dji' | 'derived' | null;
  marginRejectedReason: string | null;
  outgoingCallsign: string | null;
  /**
   * Who relieves it (stage 4 W2), or null when there is no relief half at all.
   *
   * `callsign: null` with a `unavailableReason` is NOT the same as this being null: the first
   * means a real forecast that nobody can fly (§13's COVERAGE AT RISK — the outgoing must still
   * leave at the published time), the second means there is no forecast. Rendering them the same
   * way would turn a warning into an empty space.
   */
  relief: {
    droneId: string | null;
    callsign: string | null;
    rotationOrder: number | null;
    dockId: string | null;
    unavailableReason: string | null;
    /**
     * Every aircraft the selector passed over, MINUS the one holding the station.
     *
     * The server pushes a `holding_the_station` entry for the outgoing aircraft, and — because the
     * planner only reaches the selector when a holder exists and is a member — that entry is
     * always present. Counting it made "N CHECKED" permanently one too high and put it in direct
     * contradiction with the server's own summary sentence, which filters the same entry out.
     * Filtered once, here, so nothing downstream can count it again.
     */
    rejected: Array<{ callsign: string | null; rejection: string; reasons: string[] }>;
  } | null;
  breakdown: Record<string, unknown> | null;
  resolved: Record<string, unknown> | null;
}

/**
 * The four operator controls plan §14 asks for, minus `rotate_now`.
 *
 * `rotate_now` means "launch the relief now", and nothing in this build can launch anything —
 * it lands with the orchestrator that can honour it. A button that records an intent nobody
 * acts on is worse than no button.
 */
export type RotationControl = 'approve' | 'rotate_now' | 'suspend' | 'resume' | 'abort_replacement';

export interface RotationMember {
  id: string;
  drone_id: string;
  dock_id: string | null;
  rotation_order: number;
  is_active: boolean;
  drone_callsign: string | null;
  dock_name: string | null;
}

/** Mirrors `DetachMissionOutcome` on the server — what detaching did to the MISSION. */
export type DetachMissionOutcome = 'not_running' | 'completed' | 'completion_deferred' | 'completion_failed';

export interface CoverageView {
  coverage: CoverageRow;
  members: RotationMember[];
  station: { lat: number; lon: number; altitude_m: number } | null;
}

export interface FormState {
  radiusM: string;
  stationAltitudeM: string;
  coverageTargetPct: string;
  batteryNormalPct: string;
  batteryCriticalPct: string;
  windowStart: string;
  windowEnd: string;
  memberIds: string[];
  /**
   * The one field on this form that is not a string (044).
   *
   * Every other value here is the raw contents of a text box, kept as typed so a half-entered
   * number is not silently rounded under the operator's cursor. This is a closed set chosen from a
   * radio group, so there is no half-entered state to preserve and a string would only invite a
   * fourth value nothing honours.
   */
  reliefApproach: ReliefApproach;
}

/** The achieved figure, as `GET /api/coverage/:id/achieved` returns it. */
export interface AchievedCoverage {
  window_start: string;
  window_end: string;
  window_seconds: number;
  covered_seconds: number;
  covered_pct: number;
  target_pct: number;
  meets_target: boolean;
  /** The LONGEST gaps, at most 50 of them — not necessarily all of them. See `gap_count`. */
  gaps: { started_at: string; ended_at: string; seconds: number }[];
  /**
   * How many gaps there actually were.
   *
   * THE CLIENT TYPE WAS MISSING THIS, and the panel therefore printed `gaps.length` as the gap
   * count. The service caps the list at the fifty longest, on purpose — an aircraft reporting
   * slower than the credit cap produces one gap per sample, so a four-hour window can hold
   * hundreds — so on any rotation that hit the cap the panel under-reported by however many were
   * left out, in the direction that makes coverage look better than it was.
   */
  gap_count: number;
  longest_gap_seconds: number;
  meets_max_gap_criterion: boolean;
  max_gap_criterion_seconds: number;
  fixes_examined: number;
}
